import { ENV } from '../config/env.config';

export function test() {
	return null;
}

export function getOS() {
	const { userAgent } = window.navigator;
	const { platform } = window.navigator;
	const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'];
	const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
	const iosPlatforms = ['iPhone', 'iPad', 'iPod'];
	let os = null;

	if (macosPlatforms.indexOf(platform) !== -1) {
		os = 'MacOS';
	} else if (iosPlatforms.indexOf(platform) !== -1) {
		os = 'iOS';
	} else if (windowsPlatforms.indexOf(platform) !== -1) {
		os = 'Windows';
	} else if (/Android/.test(userAgent)) {
		os = 'Android';
	} else if (!os && /Linux/.test(platform)) {
		os = 'Linux';
	}

	// @ts-ignore
	document.documentElement.setAttribute('os', os);
	return os;
}

export const hasNotch = () => {
	/**
	 * For storybook test
	 */
	const storybook = window.location !== window.parent.location;
	// @ts-ignore
	const iPhone = /iPhone/.test(navigator.userAgent) && !window.MSStream;
	const aspect = window.screen.width / window.screen.height;
	const aspectFrame = window.innerWidth / window.innerHeight;
	return (
		(iPhone && aspect.toFixed(3) === '0.462') ||
		(storybook && aspectFrame.toFixed(3) === '0.462')
	);
};

export const mergeRefs = (refs: any[]) => {
	return (value: any) => {
		refs.forEach((ref) => {
			if (typeof ref === 'function') {
				ref(value);
			} else if (ref != null) {
				ref.current = value;
			}
		});
	};
};

export const randomColor = () => {
	const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger'];

	const color = Math.floor(Math.random() * colors.length);

	return colors[color];
};

export const priceFormat = (price: number) => {
	return price.toLocaleString('en-US', {
		style: 'currency',
		currency: 'USD',
	});
};

export const average = (array: any[]) => array.reduce((a, b) => a + b) / array.length;

export const percent = (value1: number, value2: number) =>
	Number(((value1 / value2 - 1) * 100).toFixed(2));

export const getFirstLetter = (text: string, letterCount = 2): string =>
	// @ts-ignore
	text
		.toUpperCase()
		.match(/\b(\w)/g)
		.join('')
		.substring(0, letterCount);

export const debounce = (func: (arg0: any) => void, wait = 1000) => {
	let timeout: string | number | NodeJS.Timeout | undefined;

	return function executedFunction(...args: any[]) {
		const later = () => {
			clearTimeout(timeout);
			// @ts-ignore
			func(...args);
		};

		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
	};
};

// GET COMPLETE IMAGE URL
export const getImageUrl = (path?: string | null, fallback = ''): string => {
	if (!path || typeof path !== 'string') {
		return fallback;
	}
	const trimmed = path.trim();
	if (!trimmed) {
		return fallback;
	}
	if (
		trimmed.startsWith('http://') ||
		trimmed.startsWith('https://') ||
		trimmed.startsWith('data:') ||
		trimmed.startsWith('blob:')
	) {
		return trimmed;
	}

	const rawBaseUrl = ENV.API_BASE_URL || 'http://localhost:3001';
	const baseUrl = rawBaseUrl.replace(/\/api\/?$/, '').replace(/\/+$/, '');
	const cleanPath = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;

	return `${baseUrl}${cleanPath}`;
};

const GENERIC_STATUS = /^Request failed with status \d+$/;

const textOf = (value: unknown): string => {
	if (typeof value === 'string') return value.trim();
	return '';
};

const errorsText = (errors: unknown): string => {
	if (Array.isArray(errors)) {
		const first = errors.find((item) => {
			if (textOf(item)) return true;
			if (!item || typeof item !== 'object') return false;
			const row = item as Record<string, unknown>;
			return Boolean(textOf(row.msg) || textOf(row.message));
		});
		if (!first) return '';
		if (typeof first === 'string') return first.trim();
		const row = first as Record<string, unknown>;
		return textOf(row.msg) || textOf(row.message);
	}
	if (!errors || typeof errors !== 'object') return '';
	const parts: string[] = [];
	Object.values(errors as Record<string, unknown>).forEach((value) => {
		if (Array.isArray(value) && value.length) {
			const line = textOf(value[0]);
			if (line) parts.push(line);
			return;
		}
		const line = textOf(value);
		if (line) parts.push(line);
	});
	return parts.join('\n');
};

/**
 * Prefer the backend message, same as the app. Falls back only when the response has none.
 * Accepts an API body, an ApiError, nested `{ data }`, or a plain string.
 */
export const getApiMessage = (source: unknown, fallback: string): string => {
	const direct = textOf(source);
	if (direct) return direct;
	if (!source || typeof source !== 'object') return fallback;
	const obj = source as Record<string, any>;
	const fromErrors =
		errorsText(obj.errors) ||
		errorsText(obj.data?.errors) ||
		errorsText(obj.response?.data?.errors);
	if (fromErrors) return fromErrors;
	const candidates = [
		obj.message,
		obj.msg,
		obj.error,
		obj.data?.message,
		obj.data?.msg,
		obj.data?.error,
		obj.response?.data?.message,
		obj.response?.data?.msg,
		obj.response?.data?.error,
	]
		.map(textOf)
		.filter((line) => line);
	const specific = candidates.find((line) => !GENERIC_STATUS.test(line));
	if (specific) return specific;
	if (candidates.length) return candidates[0];
	return fallback;
};
